import { Inbox } from 'lucide-react';
import React, { forwardRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../../../app/store';
import { isAIEnabled, isReportsPolicyNormalizationEnabled } from '../../../../configs/logic/Slice';
import { ReportStatus, SCNReport } from '../../../../../api/scnReports';
import { useAppDispatch } from '../../../../../app/hooks';
import ActionButtonV2 from '../../../../../components/ui/button/v2/ActionButton';
import CansoEditor, { MDXEditorMethods } from '../../../../../components/ui/editor';
import { JobStatusWithSkeleton } from '../../../../../components/ui/loading/JobStatuswithSkeletonGrid';
import { formatDateForDisplay, getCurrentDate } from '../../../../../lib/utils';
import { handleSendEmail } from '../../../../../services/mailtoGenerator';
import { renderSCNEmailBody } from '../../../../../services/templating';
import '../../../../../app/markdown_editor.css';
import { createSCNReportLegacyThunk, createSCNReportThunk, fetchSinglePartySCNReportLegacyThunk, fetchSinglePartySCNReportThunk } from '../logic/scnReportsThunk';
import styles from './SCNTabContainer.module.css';
import { downloadFormats } from './utils';


interface SCNTabContainerProps {
  partyId: string;
  partyName: string;
  noticeData?: SCNReport;
  currentContent: string;
  onNoticeChange: (partyId: string, content: string) => void;
  isSaving?: boolean;
  className?: string;
}

const SCNTabContainer = forwardRef<MDXEditorMethods, SCNTabContainerProps>(({
  partyId,
  partyName,
  noticeData,
  currentContent,
  onNoticeChange,
  isSaving = false,
  className = '',
}, ref) => {
  const dispatch = useAppDispatch();
  const { id: case_id } = useParams<{ id: string }>();

  const hasContent = currentContent && currentContent.trim().length > 0;

  const isInProgress = noticeData?.status === ReportStatus.SUBMITTED ||
    noticeData?.status === ReportStatus.IN_PROGRESS;
  
  const shouldDisableButtons = !hasContent || isInProgress || isSaving;

  const isAIFlagEnabled = useAppSelector(isAIEnabled);
  const isV2Enabled = useAppSelector(isReportsPolicyNormalizationEnabled);

  const handleGenerateSCN = () => {
    if (case_id && partyId) {
        dispatch(isV2Enabled
            ? createSCNReportThunk({ case_id, request: { party_id: partyId, report_id: partyId } })
            : createSCNReportLegacyThunk({ case_id, request: { party_id: partyId, report_id: partyId } })
        );
    }
  };

  const handleRefresh = () => {
    if (case_id && partyId) {
        dispatch(isV2Enabled
            ? fetchSinglePartySCNReportThunk({ case_id, report_id: partyId })
            : fetchSinglePartySCNReportLegacyThunk({ case_id, report_id: partyId })
        );
    }
  };

  const handleSendEmailClick = async () => {
    if (!currentContent.trim() || !partyId) return;
    
    const subject = `SCN Notice - ${partyName} - ${formatDateForDisplay(getCurrentDate())}`;
    const signature = "Best regards,\nLegal Department";
    
    const emailBody = await renderSCNEmailBody(currentContent, signature);
    handleSendEmail(subject, emailBody);
  };

  const renderTitleAndActions = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
    }}>
      <div style={{
        color: '#070B24',
        fontFamily: 'Inter',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '28px',
        marginLeft: '16px',
      }}>
        Show Cause Notice Report
      </div>
      <div style={{ display: 'flex', gap: '12px', marginRight: '16px' }}>
        <ActionButtonV2
          variant="secondary"
          size="sm"
          actionType="EMAIL"
          text="Send Email"
          disabled={shouldDisableButtons}
          onClick={handleSendEmailClick}
        />
        <ActionButtonV2
          variant="secondary"
          size="sm"
          actionType="DROPDOWN"
          text="Download"
          disabled={shouldDisableButtons}
          options={downloadFormats}
          onSelect={() => {}}
        />
      </div>
    </div>
  );


  // If we have content, show Markdown editor
  if (hasContent || !isAIFlagEnabled) {
    return (
      <div className={`${styles.container} ${className}`}>
        {/* Header with action buttons */}
        {renderTitleAndActions()}

        <div className={styles.editorContainer}>
          <CansoEditor
            ref={ref}
            key={`${partyId}-remarks-editor`}
            id='scn-editor'
            markdown={currentContent}
            onChange={(value) => onNoticeChange(partyId, value || '')}
            readOnly={isSaving || false}
            className={styles.editor}
            contentEditableClassName='h-[580px]'
            placeholder='Start writing...'
            autoHideToolbar={false}
          />
        </div>
      </div>
    );
  }

  // If generation is in progress, show loading state
  if ( isInProgress && !hasContent) {
    return (
      <div className={`${styles.container} ${className}`}>
        {/* Header with action buttons */}
        {renderTitleAndActions()}
        
        <div className={styles.loadingContainer}>
          <JobStatusWithSkeleton
            title='Request Submitted'
            description='AI agent has started generating the Show Cause Notice. This may take a few minutes to complete.'
          />
          
          {/* Refresh button section */}
          <div className={styles.refreshSection}>
            <div className={styles.refreshButtonContainer}>
              <button 
                className={styles.refreshButton}
                onClick={handleRefresh}
              >
                Refresh Status
              </button>
              <div className={styles.autoRefreshContainer}>
                <div className={styles.autoRefreshIcon}>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show Generate with AI
  return (
    <div className={`${styles.container} ${className}`}>
      {/* Header with action buttons */}
      {renderTitleAndActions()}

      <div className={styles.generateContainer}>
        <div className={styles.generateCard}>
          <div className={styles.generateIcon}>
            <Inbox size={24} />
          </div>
          <div className={styles.generateTextContent}>
            <h3 className={styles.generateTitle}>Generate Show Cause Notice</h3>
            <p className={styles.generateDescription}>
              Let the Canso AI agent analyse case findings and draft the first version.
            </p>
          </div>
          <button 
            className={styles.generateButton}
            onClick={handleGenerateSCN}
          >
            Generate with AI
          </button>
        </div>
      </div>
    </div>
  );
});

export default SCNTabContainer;